/**
 * Shared Questionnaire Engine
 * Pure logic functions extracted for reuse by Questionnaire and QuestionnaireV2
 * NO UI dependencies - only business logic
 *
 * Optional schema-driven flow (no behavior change by default):
 * - For visibility/required: use schemaRules.evaluateVisibility and schemaRules.evaluateRequired
 *   with questionnaireV2Schema; wire results into your section/field rendering when migrating.
 * - For submission: pass options.normalizePayload (e.g. from schemaRules.normalizePayload) into
 *   createSubmissionPayload to normalize form data before send.
 */

import { OPT } from "../data/questionnaireOptionValues";
import {
  validateAdultSplit,
  validateKidSplit,
  validateAgeDropdowns,
} from "./validation";
import { validateAndSanitizeQuestionnaire } from "./sanitization";
import { validateFormData } from "./validation";
import { addCSRFToken } from "./csrf";
import { createRateLimitedSubmit } from "./rateLimiter";
import { submissionLock } from "./dataPersistence";
import { concurrentOperationManager } from "./raceConditionPrevention";

/**
 * Validate a single section
 * Returns { isValid: boolean, message?: string, invalidFieldId?: string }
 * @param {Object} options - Optional. visibleFieldIds: Set|string[] – only validate these fields (hidden fields do not block).
 */
export const validateCurrentSection = (section, formData, options = {}) => {
  const visibleFieldIds = options.visibleFieldIds;
  const visibleSet = visibleFieldIds != null
    ? (Array.isArray(visibleFieldIds) ? new Set(visibleFieldIds) : visibleFieldIds)
    : null;
  const isVisible = (fieldId) => visibleSet === null || visibleSet.has(fieldId);

  switch (section.id) {
    case "basicInformation":
      if (isVisible("fullName") && !formData.basicInformation?.fullName) {
        return { isValid: false, message: "Please enter your full name", invalidFieldId: "fullName" };
      }
      if (isVisible("email") && !formData.basicInformation?.email) {
        return { isValid: false, message: "Please enter your email address", invalidFieldId: "email" };
      }
      if (isVisible("startDate") && !formData.basicInformation?.startDate) {
        return { isValid: false, message: "Please select your trip start date", invalidFieldId: "startDate" };
      }
      if (isVisible("endDate") && !formData.basicInformation?.endDate) {
        return { isValid: false, message: "Please select your trip end date", invalidFieldId: "endDate" };
      }
      if (isVisible("phoneNumber") && !(formData.basicInformation?.phoneNumber && String(formData.basicInformation.phoneNumber).trim())) {
        return { isValid: false, message: "Please enter your phone number", invalidFieldId: "phoneNumber" };
      }
      break;

    case "groupInfo":
      if (isVisible("adults") && (!formData.groupInfo?.adults || formData.groupInfo.adults < 1)) {
        return { isValid: false, message: "Please enter the number of adults", invalidFieldId: "adults" };
      }

      if (isVisible("kids") && (formData.groupInfo?.kids === undefined || formData.groupInfo?.kids === null || formData.groupInfo?.kids === "")) {
        return { isValid: false, message: "Please enter the number of children (enter 0 if no children)", invalidFieldId: "kids" };
      }

      if (isVisible("numAdultMales")) {
        const adultSplitValidation = validateAdultSplit(formData);
        if (!adultSplitValidation) {
          return { isValid: false, message: "Number of adult males and females must equal total adults.", invalidFieldId: "numAdultMales" };
        }
      }

      if (formData.groupInfo?.kids > 0) {
        const numBoys = parseInt(formData.groupInfo?.numBoys ?? "", 10);
        const numGirls = parseInt(formData.groupInfo?.numGirls ?? "", 10);
        const kidsTotal = parseInt(formData.groupInfo?.kids || "0", 10);
        if (isVisible("numBoys") && (Number.isNaN(numBoys) || numBoys < 0)) {
          return { isValid: false, message: "Please select how many boys", invalidFieldId: "numBoys" };
        }
        if (isVisible("numGirls") && (Number.isNaN(numGirls) || numGirls < 0)) {
          return { isValid: false, message: "Please select how many girls", invalidFieldId: "numGirls" };
        }
        if (isVisible("numBoys") && isVisible("numGirls") && !Number.isNaN(numBoys) && !Number.isNaN(numGirls) && numBoys + numGirls !== kidsTotal) {
          return { isValid: false, message: "Number of boys and girls must equal total children", invalidFieldId: "numBoys" };
        }
        validateKidSplit(formData);

        const numBoysParsed = parseInt(formData.groupInfo?.numBoys || "0", 10);
        if (numBoysParsed > 0 && isVisible("agesOfBoys")) {
          const boysValidation = validateAgeDropdowns(
            numBoysParsed,
            formData.groupInfo?.agesOfBoys || [],
            "boy"
          );
          if (!boysValidation) {
            return { isValid: false, message: "Please select the age of each boy.", invalidFieldId: "agesOfBoys" };
          }
        }

        const numGirlsParsed = parseInt(formData.groupInfo?.numGirls || "0", 10);
        if (numGirlsParsed > 0 && isVisible("agesOfGirls")) {
          const girlsValidation = validateAgeDropdowns(
            numGirlsParsed,
            formData.groupInfo?.agesOfGirls || [],
            "girl"
          );
          if (!girlsValidation) {
            return { isValid: false, message: "Please select the age of each girl.", invalidFieldId: "agesOfGirls" };
          }
        }
      }
      break;

    case "parkTicketAccess":
      if (isVisible("ticketType") && !formData.parkTicketAccess?.ticketType) {
        return { isValid: false, message: "Please select your ticket type", invalidFieldId: "ticketType" };
      }
      if (isVisible("hasParkHopper") && !formData.parkTicketAccess?.hasParkHopper) {
        return { isValid: false, message: "Please specify if you have Park Hopper", invalidFieldId: "hasParkHopper" };
      }
      if (isVisible("specialEvents") && !formData.parkTicketAccess?.specialEvents) {
        return { isValid: false, message: "Please select your special events preference", invalidFieldId: "specialEvents" };
      }
      break;

    case "parkVisitPlans":
      if (isVisible("includeRestDays") && formData.parkVisitPlans?.includeRestDays !== OPT.YES && formData.parkVisitPlans?.includeRestDays !== OPT.NO) {
        return { isValid: false, message: "Please answer whether you'd like to include Rest days", invalidFieldId: "includeRestDays" };
      }
      if (formData.parkVisitPlans?.includeRestDays === OPT.YES) {
        if (isVisible("restDayCount") && !(formData.parkVisitPlans?.restDayCount && String(formData.parkVisitPlans.restDayCount).trim())) {
          return { isValid: false, message: "Please select how many Rest days you'd like", invalidFieldId: "restDayCount" };
        }
        if (isVisible("restDayPlacement") && !(formData.parkVisitPlans?.restDayPlacement && String(formData.parkVisitPlans.restDayPlacement).trim())) {
          return { isValid: false, message: "Please select where you'd like your Rest day(s) to fall", invalidFieldId: "restDayPlacement" };
        }
        if (isVisible("restDayStyle") && !(formData.parkVisitPlans?.restDayStyle && String(formData.parkVisitPlans.restDayStyle).trim())) {
          return { isValid: false, message: "Please select what a Rest day(s) should look like", invalidFieldId: "restDayStyle" };
        }
      }
      if (isVisible("parks") || isVisible("finalParkPreference")) {
        const totalParkDays = ["mkDays", "hsDays", "epcotDays", "akDays", "tlDays", "bbDays"]
          .reduce((total, field) => {
            const days = parseInt(formData.parkVisitPlans?.[field] || "0", 10) || 0;
            return total + days;
          }, 0);
        if (isVisible("parks") && totalParkDays === 0) {
          return { isValid: false, message: "Please select at least one park day", invalidFieldId: "parks" };
        }
        if (isVisible("finalParkPreference") && !formData.parkVisitPlans?.finalParkPreference) {
          return { isValid: false, message: "Please select your final park preference", invalidFieldId: "finalParkPreference" };
        }
      }
      break;

    case "accommodation":
      if (isVisible("transportation") && !formData.accommodation?.transportation) {
        return { isValid: false, message: "Please select your transportation method", invalidFieldId: "transportation" };
      }
      if (isVisible("stayingOnProperty") && !formData.accommodation?.stayingOnProperty) {
        return { isValid: false, message: "Please specify if you're staying on property", invalidFieldId: "stayingOnProperty" };
      }
      if (isVisible("resort") && formData.accommodation?.stayingOnProperty && formData.accommodation.stayingOnProperty !== OPT.OFF_DISNEY_PROPERTY) {
        if (!(formData.accommodation?.resort && String(formData.accommodation.resort).trim())) {
          return { isValid: false, message: "Please select your Disney resort", invalidFieldId: "resort" };
        }
      }
      if (isVisible("otherHotel") && formData.accommodation?.stayingOnProperty === OPT.OFF_DISNEY_PROPERTY) {
        if (!(formData.accommodation?.otherHotel && String(formData.accommodation.otherHotel).trim())) {
          return { isValid: false, message: "Please specify your off-property hotel name", invalidFieldId: "otherHotel" };
        }
      }
      break;

    case "attractionRideStrategy":
      if (isVisible("lightningLane") && (!formData.attractionRideStrategy?.lightningLane ||
        (typeof formData.attractionRideStrategy.lightningLane === 'string' && formData.attractionRideStrategy.lightningLane.trim() === "") ||
        formData.attractionRideStrategy.lightningLane === "")) {
        return { isValid: false, message: "Please select your Lightning Lane preference", invalidFieldId: "lightningLane" };
      }
      if (isVisible("rideStrategy") && !(formData.attractionRideStrategy?.rideStrategy && String(formData.attractionRideStrategy.rideStrategy).trim())) {
        return { isValid: false, message: "Please select a ride strategy", invalidFieldId: "rideStrategy" };
      }

      const rideStrategyVal = formData.attractionRideStrategy?.rideStrategy;
      const isParkCompletionist = rideStrategyVal === OPT.PARK_COMPLETIONIST;

      if (!isParkCompletionist && isVisible("ridesBucketSelector")) {
        const mustRidesByPark = formData.attractionRideStrategy?.mustRidesByPark || {};
        const totalMust = Object.values(mustRidesByPark).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0);
        if (totalMust === 0) {
          return { isValid: false, message: "Please select at least one ride", invalidFieldId: "ridesBucketSelector" };
        }
      }
      break;

    case "specialConsiderations":
      if (isVisible("celebration") && !formData.specialConsiderations?.celebration) {
        return { isValid: false, message: "Please answer whether you're celebrating anything special", invalidFieldId: "celebration" };
      }
      if (isVisible("celebrationDetails") && formData.specialConsiderations?.celebration === OPT.YES && !formData.specialConsiderations?.celebrationDetails?.trim()) {
        return { isValid: false, message: "Please tell us what you're celebrating", invalidFieldId: "celebrationDetails" };
      }
      if (isVisible("celebrationDate") && formData.specialConsiderations?.celebration === OPT.YES && !(formData.specialConsiderations?.celebrationDate && String(formData.specialConsiderations.celebrationDate).trim())) {
        return { isValid: false, message: "Please enter the date of the celebration", invalidFieldId: "celebrationDate" };
      }
      if (isVisible("celebrationOtherDetails") && formData.specialConsiderations?.celebrationDetails === OPT.OTHER && !(formData.specialConsiderations?.celebrationOtherDetails && String(formData.specialConsiderations.celebrationOtherDetails).trim())) {
        return { isValid: false, message: "Please tell us what specifically you're celebrating", invalidFieldId: "celebrationOtherDetails" };
      }
      if (isVisible("hasDisability") && !formData.specialConsiderations?.hasDisability) {
        return { isValid: false, message: "Please answer whether anyone has a disability or mobility concern", invalidFieldId: "hasDisability" };
      }
      if (isVisible("disabilities") && formData.specialConsiderations?.hasDisability === OPT.YES && !formData.specialConsiderations?.disabilities?.trim()) {
        return { isValid: false, message: "Please explain the disability or mobility concerns so we can accommodate your needs", invalidFieldId: "disabilities" };
      }
      if (isVisible("additionalInfo") && !formData.specialConsiderations?.additionalInfo) {
        return { isValid: false, message: "Please answer whether there's anything else we should know", invalidFieldId: "additionalInfo" };
      }
      if (isVisible("additionalInfoDetails") && formData.specialConsiderations?.additionalInfo === OPT.YES && !formData.specialConsiderations?.additionalInfoDetails?.trim()) {
        return { isValid: false, message: "Please tell us what additional information we should know", invalidFieldId: "additionalInfoDetails" };
      }
      break;

    case "diningPreferences":
      if (isVisible("diningPlanInterest") && formData.diningPreferences?.diningPlanInterest !== OPT.YES && formData.diningPreferences?.diningPlanInterest !== OPT.NO) {
        return { isValid: false, message: "Please answer whether you're interested in a Disney Dining Plan", invalidFieldId: "diningPlanInterest" };
      }
      if (isVisible("diningTime")) {
        const diningTimeVal = formData.diningPreferences?.diningTime;
        const hasDiningTime = Array.isArray(diningTimeVal) ? diningTimeVal.length > 0 : (diningTimeVal && String(diningTimeVal).trim());
        if (!hasDiningTime) {
          return { isValid: false, message: "Please select at least one preferred dining time", invalidFieldId: "diningTime" };
        }
      }
      if (isVisible("restaurantStrategy") && !(formData.diningPreferences?.restaurantStrategy && String(formData.diningPreferences.restaurantStrategy).trim())) {
        return { isValid: false, message: "Please select a restaurant strategy", invalidFieldId: "restaurantStrategy" };
      }
      if (isVisible("restaurantsBucketSelector")) {
        const mustByPark = formData.diningPreferences?.mustTryRestaurantsByPark || {};
        const totalMustTry = Object.values(mustByPark).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0);
        if (totalMustTry === 0) {
          return { isValid: false, message: "Please select at least one restaurant", invalidFieldId: "restaurantsBucketSelector" };
        }
      }
      if (isVisible("allergies") && (!formData.diningPreferences?.allergies || formData.diningPreferences.allergies.length === 0)) {
        return { isValid: false, message: "Please select your food allergies (select 'None' if you have no allergies)", invalidFieldId: "allergies" };
      }
      break;

    case "showFireworkStrategy":
      if (isVisible("showStrategy") && !(formData.showFireworkStrategy?.showStrategy && String(formData.showFireworkStrategy.showStrategy).trim())) {
        return { isValid: false, message: "Please select a show & entertainment interest", invalidFieldId: "showStrategy" };
      }

      const showStrategyVal = formData.showFireworkStrategy?.showStrategy;
      const isNotInterested = showStrategyVal === OPT.NOT_INTERESTED;

      if (!isNotInterested && isVisible("showsBucketSelector")) {
        const mustByPark = formData.showFireworkStrategy?.mustSeeShowsByPark || {};
        const totalMustSee = Object.values(mustByPark).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0);
        if (totalMustSee === 0) {
          return { isValid: false, message: "Please select at least one show or entertainment option", invalidFieldId: "showsBucketSelector" };
        }
      }
      if (isVisible("fantasmicDiningPackageInterest")) {
        const interest = formData.showFireworkStrategy?.fantasmicDiningPackageInterest;
        if (!interest || (typeof interest === "string" && !interest.trim())) {
          return { isValid: false, message: "Please answer whether you're interested in the Fantasmic! Dining Package", invalidFieldId: "fantasmicDiningPackageInterest" };
        }
        if (interest === OPT.YES && isVisible("fantasmicDiningPackageRestaurants")) {
          const restaurants = formData.showFireworkStrategy?.fantasmicDiningPackageRestaurants;
          if (!restaurants || (Array.isArray(restaurants) && restaurants.length === 0)) {
            return { isValid: false, message: "Please select at least one Fantasmic! Dining Package restaurant", invalidFieldId: "fantasmicDiningPackageRestaurants" };
          }
        }
      }
      break;

    case "timeEnergy":
      if (isVisible("pacing") && !(formData.timeEnergy?.pacing && String(formData.timeEnergy.pacing).trim())) {
        return { isValid: false, message: "Please select how you'd like your park days to feel", invalidFieldId: "pacing" };
      }
      if (isVisible("parkStartTime") && (!formData.timeEnergy?.parkStartTime || formData.timeEnergy.parkStartTime.length === 0)) {
        return { isValid: false, message: "Please select your preferred park entry time", invalidFieldId: "parkStartTime" };
      }
      if (isVisible("breakStyle") && !(formData.timeEnergy?.breakStyle && String(formData.timeEnergy.breakStyle).trim())) {
        return { isValid: false, message: "Please select how you'd like to structure your park day", invalidFieldId: "breakStyle" };
      }
      if (isVisible("leaveParkMiddayWhere") && formData.timeEnergy?.breakStyle === OPT.MIDDAY_BREAK && !(formData.timeEnergy?.leaveParkMiddayWhere && String(formData.timeEnergy.leaveParkMiddayWhere).trim())) {
        return { isValid: false, message: "Please select where you'd go if leaving midday", invalidFieldId: "leaveParkMiddayWhere" };
      }
      break;

    case "characterMeetGreets":
      if (isVisible("interestedInCharacterMeetGreets") && !(formData.characterMeetGreets?.interestedInCharacterMeetGreets && String(formData.characterMeetGreets.interestedInCharacterMeetGreets).trim())) {
        return { isValid: false, message: "Please answer whether you're interested in Character Meet & Greets", invalidFieldId: "interestedInCharacterMeetGreets" };
      }
      if (formData.characterMeetGreets?.interestedInCharacterMeetGreets === OPT.YES && isVisible("mustMeetCharacters")) {
        const chars = formData.characterMeetGreets?.mustMeetCharacters;
        if (!chars || (Array.isArray(chars) && chars.length === 0)) {
          return { isValid: false, message: "Please select at least one character to meet", invalidFieldId: "mustMeetCharacters" };
        }
      }
      break;

    case "specialtyExperiences":
      if (isVisible("enchantedExperiences") && (!formData.specialtyExperiences?.enchantedExperiences ||
        (typeof formData.specialtyExperiences.enchantedExperiences === 'string' && formData.specialtyExperiences.enchantedExperiences.trim() === "") ||
        formData.specialtyExperiences.enchantedExperiences === "")) {
        return { isValid: false, message: "Please answer whether you're interested in enchanted experiences", invalidFieldId: "enchantedExperiences" };
      }
      if (isVisible("mustExperience") && formData.specialtyExperiences?.enchantedExperiences === OPT.YES &&
        (!formData.specialtyExperiences?.mustExperience || formData.specialtyExperiences.mustExperience.length === 0)) {
        return { isValid: false, message: "Please select at least one premium experience", invalidFieldId: "mustExperience" };
      }
      break;

    default:
      if (section.fields) {
        for (const field of section.fields) {
          if (!isVisible(field.id)) continue;
          if (field.dependsOn) {
            const dependentField = formData[section.id]?.[field.dependsOn.field];
            if (dependentField !== field.dependsOn.value) {
              continue;
            }
          }

          const fieldValue = formData[section.id]?.[field.id];
          if (field.required === false) continue;
          const isEmpty = fieldValue === undefined || fieldValue === null ||
            (typeof fieldValue === "string" && !fieldValue.trim()) ||
            (Array.isArray(fieldValue) && fieldValue.length === 0);
          if (isEmpty) {
            return { isValid: false, message: `Please complete: ${field.label}`, invalidFieldId: field.id };
          }
        }
      }
      break;
  }

  return { isValid: true };
};

export const checkParkDaysWarning = (formData) => {
  const totalParkDays = ["mkDays", "hsDays", "epcotDays", "akDays", "tlDays", "bbDays"]
    .reduce((total, field) => {
      const days = parseInt(formData.parkVisitPlans?.[field] || "0", 10) || 0;
      return total + days;
    }, 0);

  const start = new Date(formData.basicInformation?.startDate);
  const end = new Date(formData.basicInformation?.endDate);
  if (isNaN(start) || isNaN(end)) return null;

  const totalTripDays = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
  const eligibleParkDays = Math.max(totalTripDays - 2, 0);
  const isAnnualPass = formData.parkTicketAccess?.ticketType === OPT.ANNUAL_PASS;
  const isHopper = formData.parkTicketAccess?.hasParkHopper === OPT.YES;
  const allowExtraParkDays = isAnnualPass || isHopper;

  if (totalParkDays > eligibleParkDays) {
    return {
      shouldWarn: true,
      allowExtraParkDays,
      totalParkDays,
      eligibleParkDays
    };
  }

  return null;
};

export const shouldShowRecap = (sectionId) => {
  return ["attractionRideStrategy", "showFireworkStrategy", "diningPreferences"].includes(sectionId);
};

export const findFirstInvalidField = (sections, formData, visibleFieldIdsBySection = null) => {
  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];
    const visibleIds = visibleFieldIdsBySection?.[section.id] ?? section.fields?.map((f) => f.id) ?? [];
    const validation = validateCurrentSection(section, formData, { visibleFieldIds: visibleIds });
    if (!validation.isValid) {
      return {
        sectionIndex: i,
        fieldId: validation.invalidFieldId || section.fields?.[0]?.id,
        message: validation.message,
        sectionId: section.id
      };
    }
  }
  return null;
};

export const createSubmissionPayload = (formData, tripId, isValidUUID, options = {}) => {
  const rawData = typeof options.normalizePayload === "function" ? options.normalizePayload(formData) : formData;
  const sanitizedData = validateAndSanitizeQuestionnaire(rawData);
  // Align with legacy Questionnaire: apply rest-day defaults when includeRestDays is Yes (backend may expect these)
  const pvp = sanitizedData.parkVisitPlans || {};
  const includeRest = pvp.includeRestDays === "Yes";
  const parkVisitPlansWithDefaults = includeRest
    ? {
        ...pvp,
        restDayPlacement: (pvp.restDayPlacement && String(pvp.restDayPlacement).trim()) || "Middle of trip",
        restDayStyle: (pvp.restDayStyle && String(pvp.restDayStyle).trim()) || "Light Day (easy pace)",
        restDayCount: (pvp.restDayCount && String(pvp.restDayCount).trim()) || "1",
      }
    : { ...pvp, restDayPlacement: "", restDayStyle: "", restDayCount: "" };
  const finalData = {
    ...sanitizedData,
    parkVisitPlans: parkVisitPlansWithDefaults,
    clientName: sanitizedData.basicInformation?.fullName || "",
  };
  return {
    questionnaireResponses: finalData,
    ...(isValidUUID(tripId) && { tripId }),
  };
};

export const createQuestionnaireSubmit = ({
  formData,
  tripId,
  isValidUUID,
  formManager,
  executeWithErrorHandling,
  fetchWithOptimization,
  setModalMessage,
  setDelayedQuote,
  setError,
  setIsSuccess,
  setSubmissionStartTime,
  setTimeRemaining,
  setLoading,
  toast,
  normalizePayload: normalizePayloadFn,
  sectionsForValidation = null,
  visibleFieldIdsBySection = null,
}) => {
  const formId = `questionnaire-${tripId}`;

  return createRateLimitedSubmit(async () => {
    return await concurrentOperationManager.executeOperation(
      `submit-questionnaire-${formId}`,
      async () => {
        if (!submissionLock.acquireLock(formId)) {
          throw new Error("Form is already being submitted. Please wait.");
        }

        try {
          const showSubmissionMessage = () => {
            setModalMessage("Submitting your trip info...");
            setTimeout(() => {
              const quotes = [
                "Even miracles take a little time... – Cinderella",
                "Laughter is timeless, imagination has no age, and dreams are forever.",
              ];
              const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
              setDelayedQuote(randomQuote);
            }, 2000);
          };

          showSubmissionMessage();

          if (sectionsForValidation && sectionsForValidation.length > 0 && visibleFieldIdsBySection != null) {
            const firstInvalid = findFirstInvalidField(sectionsForValidation, formData, visibleFieldIdsBySection);
            if (firstInvalid) {
              throw new Error(firstInvalid.message);
            }
          } else {
            const validationResult = validateFormData(formData);
            if (!validationResult.isValid) {
              const errorMessage = validationResult.errors.length === 1
                ? validationResult.errors[0]
                : `Please complete the following required fields:\n• ${validationResult.errors.join('\n• ')}`;
              throw new Error(errorMessage);
            }
          }

          const payloadOptions = typeof normalizePayloadFn === "function" ? { normalizePayload: normalizePayloadFn } : {};
          const requestPayload = createSubmissionPayload(formData, tripId, isValidUUID, payloadOptions);

          formManager.lockForm();
          setSubmissionStartTime(Date.now());

          const res = await executeWithErrorHandling(async () => {
            return await fetchWithOptimization(
              `${import.meta.env.VITE_API_URL || ""}/api/itineraries/public`,
              {
                method: "POST",
                credentials: 'include',
                headers: {
                  "Content-Type": "application/json",
                  ...addCSRFToken()
                },
                body: JSON.stringify(requestPayload),
              }
            );
          });

          const data = await res.json();

          if (!res.ok) {
            let errorMessage = "We're having trouble processing your request.";

            if (data.message) {
              if (data.message.toLowerCase().includes("limit") || data.code === "ITINERARY_LIMIT_REACHED") {
                errorMessage = "You've reached your itinerary limit for this period. Please upgrade your plan or try again later.";
              } else if (data.message.includes("validation")) {
                errorMessage = "Please check your form information and try again. Make sure all required fields are filled out correctly.";
              } else if (data.message.includes("server")) {
                errorMessage = "Our servers are experiencing high traffic. Please try again in a few minutes.";
              } else {
                errorMessage = data.message;
              }
            } else if (res.status === 403) {
              errorMessage = "You've reached your itinerary limit for this period. Please upgrade your plan or try again later.";
            } else if (res.status === 500) {
              errorMessage = "We're experiencing technical difficulties. Please try again in a few minutes or contact your travel agent for assistance.";
            } else if (res.status === 429) {
              errorMessage = "Please wait a moment before submitting again. This helps us ensure the best experience for all users.";
            }

            throw new Error(errorMessage);
          }

          setIsSuccess(true);
          setModalMessage("success");
          formManager.markSubmitted(true);
          setSubmissionStartTime(null);
          setTimeRemaining(null);
          setLoading(false);
          toast.success("Your trip information has been submitted successfully!", { duration: 4000 });
        } catch (err) {
          const errorMessage = err.message || "We encountered an unexpected issue. Please try again or contact your travel agent for assistance.";

          setError(errorMessage);
          setModalMessage("error");
          setLoading(false);
          throw err;
        } finally {
          formManager.unlockForm();
          submissionLock.releaseLock(formId);
          setSubmissionStartTime(null);
          setTimeRemaining(null);
        }
      }
    );
  }, `questionnaire-${tripId}`);
};

export const calculateQualityScore = (formData, sections) => {
  if (!formData || !sections) return 0;

  let totalFields = 0;
  let answeredFields = 0;
  const missingFields = [];

  sections.forEach(section => {
    if (section.fields) {
      section.fields.forEach(field => {
        if (field.dependsOn) {
          const dependentValue = formData[section.id]?.[field.dependsOn.field];
          if (dependentValue !== field.dependsOn.value) {
            return;
          }
        }

        if (field.dynamicFrom) {
          const dynamicValue = formData[section.id]?.[field.dynamicFrom];
          const dynamicCount = parseInt(dynamicValue || "0", 10) || 0;
          if (dynamicCount === 0) {
            return;
          }
        }

        if (field.type === 'info' || field.type === 'resortBenefitsNote') {
          return;
        }

        if (field.id === 'tlDays' || field.id === 'bbDays') {
          return;
        }

        if (field.required !== false) {
          totalFields++;
          const value = formData[section.id]?.[field.id];

          const isAnswered =
            value !== undefined &&
            value !== null &&
            value !== "" &&
            (!Array.isArray(value) || value.length > 0) &&
            (typeof value !== 'number' || value >= 0);

          if (isAnswered) {
            answeredFields++;
          } else {
            missingFields.push({
              section: section.id,
              field: field.id,
              label: field.label,
              value: value,
              type: Array.isArray(value) ? 'array' : typeof value
            });
          }
        }
      });
    }
  });

  if (process.env.NODE_ENV === 'development' && missingFields.length > 0) {
    console.log('📊 Quality Score Debug:', {
      totalFields,
      answeredFields,
      score: totalFields > 0 ? Math.round((answeredFields / totalFields) * 100) : 0,
      missingFields: missingFields.map(f => `${f.section}.${f.field} (${f.label}) - value: ${JSON.stringify(f.value)}`)
    });
  }

  return totalFields > 0 ? Math.round((answeredFields / totalFields) * 100) : 0;
};
